import { TaskReceiptAction } from "@/features/task-receipt-action";
import { taskLabel } from "@/lib/task-reference";
import { TaskReferenceDisplay } from "@/components/task-reference-display";
import { useRef, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";

import {
  EmptyRow,
  ErrorRow,
  LoadingRows,
  TablePager,
  TableToolbar,
  usePagedState,
} from "@/components/data-table";
import { PageHeader } from "@/components/page-header";
import { StatusBadge, humanizeStatus } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { NativeSelect } from "@/components/ui/native-select";
import { RowMenu, RowMenuItem } from "@/components/ui/row-menu";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type CarrierTask,
  useCarrierTasks,
  useCompleteCarrierTask,
  useReceiveTask,
  useRemoveTasksFromTrip,
} from "@/api/carrier";
import { AssignTaskToTripDialog } from "@/features/assign-task-to-trip-dialog";
import { AssignTaskOperatorDialog } from "@/features/assign-task-operator-dialog";
import { ReassignTaskDialog } from "@/features/reassign-task-dialog";
import { TaskDetailDialog } from "@/features/task-detail-dialog";
import {
  formatTaskPickupWindow,
  formatTaskReadyAt,
  formatTaskScheduleDay,
} from "@/features/task-schedule-display";
import { hasHttpStatus } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { operatorDisplayName } from "@/lib/operator-identity";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";

const COLUMNS = 9;

const STATUS_FILTERS = [
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
] as const;

const TYPE_FILTERS = [
  "CUSTOMER_PICKUP",
  "CUSTOMER_DELIVERY",
  "CUSTOMER_RETURN",
  "CARRIER_DROPOFF",
  "CARRIER_PICKUP",
  "DEPOT_RECEIVE",
  "DEPOT_SORT",
  "DEPOT_DISPATCH",
  "DEPOT_TRANSFER",
] as const;

const TRIP_FILTERS = [
  { value: "", label: "All trips" },
  { value: "ASSIGNED", label: "Assigned" },
  { value: "UNASSIGNED", label: "Unassigned" },
  { value: "PLANNED", label: "Trip planned" },
  { value: "IN_PROGRESS", label: "Trip in progress" },
  { value: "COMPLETED", label: "Trip completed" },
  { value: "CANCELLED", label: "Trip cancelled" },
] as const;

type ConfirmationTarget =
  | { action: "complete"; task: CarrierTask }
  | { action: "unassign"; task: CarrierTask };

function personName(task: Pick<CarrierTask, "operator" | "trip">): string {
  const carrierUser =
    task.operator?.carrierUser ?? task.trip?.operator?.carrierUser;
  if (!carrierUser) return "Unassigned operator";
  return operatorDisplayName({ carrierUser });
}

function dispatchCenterName(task: CarrierTask): string {
  return (
    task.dispatchCenter?.name ?? task.depot?.name ?? "Unknown Dispatch Center"
  );
}

function canComplete(task: CarrierTask): boolean {
  return (
    !isTaskOnCancellationHold(task) &&
    (task.status === "PENDING" || task.status === "IN_PROGRESS")
  );
}

function secondaryTiming(task: CarrierTask): string {
  if (task.receivedAt) return `Received ${formatDate(task.receivedAt)}`;
  if (task.completedAt) return `Completed ${formatDate(task.completedAt)}`;
  if (task.startedAt) return `Started ${formatDate(task.startedAt)}`;
  const pickupWindow = formatTaskPickupWindow(task);
  if (pickupWindow) return `Pickup ${pickupWindow}`;
  if (task.readyAt) return `Ready ${formatTaskReadyAt(task)}`;
  if (task.scheduledDate) return `Scheduled ${formatTaskScheduleDay(task)}`;
  return "Not started";
}

export default function TasksScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const awaitingReceipt = searchParams.get("view") === "awaiting-receipt";
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [tripStatus, setTripStatus] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { page, pageSize, search, setSearch, setPage, setPageSize, resetPage } =
    usePagedState({ initialSearch: searchParams.get("q") ?? "" });

  const { data, isLoading, error, refetch } = useCarrierTasks({
    awaitingReceipt,
    page,
    limit: pageSize,
    ...(!awaitingReceipt && search && { q: search }),
    ...(!awaitingReceipt && status && { status }),
    ...(!awaitingReceipt && type && { type }),
    ...(!awaitingReceipt && tripStatus && { tripStatus }),
  });
  const receive = useReceiveTask();
  const [receiving, setReceiving] = useState(false);
  const completeTask = useCompleteCarrierTask();
  const removeFromTrip = useRemoveTasksFromTrip();
  const [reassignTask, setReassignTask] = useState<CarrierTask | null>(null);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(() =>
    searchParams.get("taskId")
  );
  const [assignTrip, setAssignTrip] = useState<CarrierTask | null>(null);
  const [assignOperator, setAssignOperator] = useState<CarrierTask | null>(
    null
  );
  const [confirmation, setConfirmation] = useState<ConfirmationTarget | null>(
    null
  );
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const actionLocks = useRef(new Set<string>());

  const rows = data?.data ?? [];
  const showRows = !isLoading && Boolean(data);
  const selectedTask = rows.find((task) => task.id === detailTaskId);
  const activeFilterCount =
    Number(Boolean(tripStatus)) +
    Number(Boolean(type)) +
    Number(Boolean(status));
  const hasDiscoveryFilters = Boolean(
    search.trim() || tripStatus || type || status
  );
  const actionsPending =
    receive.isPending || completeTask.isPending || removeFromTrip.isPending;

  const closeTaskDetail = () => {
    setDetailTaskId(null);
    if (searchParams.has("taskId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("taskId");
      setSearchParams(next, { replace: true });
    }
  };

  const switchView = (nextAwaitingReceipt: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (nextAwaitingReceipt) next.set("view", "awaiting-receipt");
    else next.delete("view");
    setSearchParams(next, { replace: true });
    resetPage();
  };

  const beginAction = (key: string) => {
    if (actionLocks.current.has(key)) return false;
    actionLocks.current.add(key);
    setActionError(null);
    setActionStatus(null);
    return true;
  };

  const handleReceive = (
    task: CarrierTask,
    center: { id: string; name: string }
  ) => {
    if (
      task.canReceiveCustomerDropoff === false ||
      isTaskOnCancellationHold(task) ||
      actionLocks.current.size > 0
    )
      return;
    const key = `receive:${task.id}`;
    if (!beginAction(key)) return;
    setReceiving(true);
    receive.mutate(
      { taskId: task.id, dispatchCenterId: center.id },
      {
        onSuccess: (response) => {
          const message =
            response.data.changed === false
              ? `${taskLabel(task)} was already received. Check Task details for its receiving center.`
              : `${task.shipment ? `SHP-${task.shipment.shipmentNumber}` : taskLabel(task)} received at ${center.name}.`;
          setActionStatus(message);
          toast.success(message);
        },
        onError: () => {
          setActionError(
            `Receipt for ${taskLabel(task)} at ${center.name} could not be confirmed. Refresh Tasks and check receipt status before retrying.`
          );
          toast.error("Receipt could not be confirmed. Check Task status.");
        },
        onSettled: () => {
          actionLocks.current.delete(key);
          setReceiving(false);
        },
      }
    );
  };

  const handleUnassign = (task: CarrierTask) => {
    if (!task.tripId || isTaskOnCancellationHold(task)) return;
    const key = `unassign:${task.id}`;
    if (!beginAction(key)) return;
    removeFromTrip.mutate(
      { tripId: task.tripId, taskIds: [task.id] },
      {
        onSuccess: () => {
          setActionStatus(`${taskLabel(task)} unassigned from its Trip.`);
          setConfirmation(null);
          toast.success(`${taskLabel(task)} removed from trip.`);
        },
        onError: () => {
          setActionError(
            `${taskLabel(task)} could not be unassigned. Please retry.`
          );
          toast.error("Failed to remove task from trip.");
        },
        onSettled: () => actionLocks.current.delete(key),
      }
    );
  };

  const handleComplete = (task: CarrierTask) => {
    if (isTaskOnCancellationHold(task)) return;
    const key = `complete:${task.id}`;
    if (!beginAction(key)) return;
    completeTask.mutate(
      { taskId: task.id },
      {
        onSuccess: () => {
          setActionStatus(`${taskLabel(task)} completed.`);
          setConfirmation(null);
          toast.success(`${taskLabel(task)} completed.`);
        },
        onError: () => {
          setActionError(
            `${taskLabel(task)} could not be completed. Please retry.`
          );
          toast.error("Failed to complete task.");
        },
        onSettled: () => actionLocks.current.delete(key),
      }
    );
  };

  const requestAssign = (task: CarrierTask) => {
    if (isTaskOnCancellationHold(task)) return;
    closeTaskDetail();
    setAssignTrip(task);
  };

  const requestReassign = (task: CarrierTask) => {
    if (isTaskOnCancellationHold(task)) return;
    closeTaskDetail();
    setReassignTask(task);
  };

  const requestAssignOperator = (task: CarrierTask) => {
    if (isTaskOnCancellationHold(task)) return;
    closeTaskDetail();
    setAssignOperator(task);
  };

  const requestUnassign = (task: CarrierTask) => {
    if (isTaskOnCancellationHold(task)) return;
    setConfirmation({ action: "unassign", task });
  };

  const requestComplete = (task: CarrierTask) => {
    if (isTaskOnCancellationHold(task)) return;
    setConfirmation({ action: "complete", task });
  };

  return (
    <div className="carrier-page">
      <PageHeader title={awaitingReceipt ? "Awaiting receipt" : "Tasks"} />

      {actionError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
        >
          {actionError}
        </div>
      )}
      {actionStatus && (
        <div
          role="status"
          className="rounded-md border bg-muted/40 p-3 text-sm"
        >
          {actionStatus}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="space-y-3 border-b p-4">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:w-fit">
            <Button
              type="button"
              variant={awaitingReceipt ? "outline" : "default"}
              size="touch"
              onClick={() => switchView(false)}
            >
              All Tasks
            </Button>
            <Button
              type="button"
              variant={awaitingReceipt ? "default" : "outline"}
              size="touch"
              onClick={() => switchView(true)}
            >
              Awaiting receipt
            </Button>
          </div>

          {!awaitingReceipt && (
            <TableToolbar
              className="mb-0"
              search={search}
              onSearchChange={(value) => {
                resetPage();
                setSearch(value);
              }}
              searchPlaceholder="Task, Shipment, contact..."
              searchAriaLabel="Search Tasks"
            >
              <div className="hidden lg:block">
                <TaskFilterFields
                  tripStatus={tripStatus}
                  type={type}
                  status={status}
                  onTripStatusChange={(value) => {
                    resetPage();
                    setTripStatus(value);
                  }}
                  onTypeChange={(value) => {
                    resetPage();
                    setType(value);
                  }}
                  onStatusChange={(value) => {
                    resetPage();
                    setStatus(value);
                  }}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="touch"
                className="w-full sm:w-auto lg:hidden"
                onClick={() => setFiltersOpen(true)}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
              </Button>
            </TableToolbar>
          )}
        </div>

        <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
          <SheetContent
            side="right"
            className="flex w-full flex-col p-0 sm:max-w-sm"
          >
            <SheetHeader className="border-b px-4 py-4 pr-12 text-left">
              <SheetTitle>Task filters</SheetTitle>
              <SheetDescription>
                Narrow the carrier-visible Task queue.
              </SheetDescription>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-4">
              <TaskFilterFields
                mobile
                tripStatus={tripStatus}
                type={type}
                status={status}
                onTripStatusChange={(value) => {
                  resetPage();
                  setTripStatus(value);
                }}
                onTypeChange={(value) => {
                  resetPage();
                  setType(value);
                }}
                onStatusChange={(value) => {
                  resetPage();
                  setStatus(value);
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-2 border-t p-4">
              <Button
                type="button"
                variant="outline"
                size="touch"
                disabled={activeFilterCount === 0}
                onClick={() => {
                  setTripStatus("");
                  setType("");
                  setStatus("");
                  resetPage();
                }}
              >
                Clear filters
              </Button>
              <Button
                type="button"
                size="touch"
                onClick={() => setFiltersOpen(false)}
              >
                Show Tasks
              </Button>
            </div>
          </SheetContent>
        </Sheet>

        {error && data && (
          <div
            role="alert"
            className="border-b border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            {hasHttpStatus(error, 403)
              ? "You do not have permission to refresh Tasks. Cached Tasks remain visible."
              : "Tasks could not be refreshed. Cached Tasks remain visible."}
          </div>
        )}

        <div className="divide-y md:hidden">
          {isLoading && (
            <div
              role="status"
              aria-label="Loading Tasks"
              className="space-y-2 p-3"
            >
              {["task-skeleton-1", "task-skeleton-2", "task-skeleton-3"].map(
                (key) => (
                  <div
                    key={key}
                    className="h-28 animate-pulse rounded-md bg-muted"
                  />
                )
              )}
            </div>
          )}
          {error && !data && (
            <div
              role="alert"
              className="space-y-3 p-4 text-sm text-destructive"
            >
              <p>
                {hasHttpStatus(error, 403)
                  ? "You do not have permission to view Tasks."
                  : "Failed to load Tasks."}
              </p>
              {!hasHttpStatus(error, 403) && (
                <Button
                  type="button"
                  variant="outline"
                  size="touch"
                  onClick={() => refetch()}
                >
                  Retry
                </Button>
              )}
            </div>
          )}
          {showRows && rows.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {awaitingReceipt
                ? "Nothing awaiting receipt."
                : hasDiscoveryFilters
                  ? "No Tasks match your filters."
                  : "No Tasks yet."}
            </div>
          )}
          {showRows &&
            rows.map((task) => (
              <article key={task.id} className="relative space-y-2 p-3">
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`View Task ${taskLabel(task)}`}
                  className="absolute inset-0 h-full w-full rounded-none p-0"
                  onClick={() => setDetailTaskId(task.id)}
                />
                <div className="pointer-events-none relative space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <TaskReferenceDisplay task={task} className="flex-1" />
                    <StatusBadge status={task.status} />
                  </div>
                  <p className="truncate text-xs text-muted-foreground">
                    {humanizeStatus(task.type)} · {secondaryTiming(task)}
                  </p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <span>
                      {task.shipment
                        ? `SHP-${task.shipment.shipmentNumber}`
                        : "No Shipment"}
                    </span>
                    <span className="truncate text-right">
                      {task.trip ? `TRP-${task.trip.tripNumber}` : "No Trip"}
                    </span>
                    <span className="truncate text-muted-foreground">
                      {personName(task)}
                    </span>
                    <span className="truncate text-right text-muted-foreground">
                      {dispatchCenterName(task)}
                    </span>
                  </div>
                </div>
                {awaitingReceipt && (
                  <TaskReceiptAction
                    task={task}
                    pending={receiving || receive.isPending}
                    onReceive={handleReceive}
                  />
                )}
              </article>
            ))}
        </div>

        <div className="hidden overflow-x-auto md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Task</TableHead>
                <TableHead className="whitespace-nowrap">Type</TableHead>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <TableHead className="whitespace-nowrap">Timing</TableHead>
                <TableHead className="whitespace-nowrap">Shipment</TableHead>
                <TableHead className="whitespace-nowrap">Trip</TableHead>
                <TableHead className="whitespace-nowrap">
                  Dispatch Center
                </TableHead>
                <TableHead className="whitespace-nowrap">Operator</TableHead>
                <TableHead className="w-12" aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <LoadingRows columns={COLUMNS} />}
              {error && !data && (
                <ErrorRow
                  columns={COLUMNS}
                  message={
                    hasHttpStatus(error, 403)
                      ? "You do not have permission to view Tasks."
                      : "Failed to load Tasks."
                  }
                  onRetry={
                    hasHttpStatus(error, 403) ? undefined : () => refetch()
                  }
                />
              )}
              {showRows && rows.length === 0 && (
                <EmptyRow columns={COLUMNS}>
                  {awaitingReceipt
                    ? "Nothing awaiting receipt."
                    : hasDiscoveryFilters
                      ? "No Tasks match your filters."
                      : "No Tasks yet."}
                </EmptyRow>
              )}
              {showRows &&
                rows.map((task) => (
                  <TableRow
                    key={task.id}
                    className={`cursor-pointer ${isTaskOnCancellationHold(task) ? "bg-muted/50 text-muted-foreground" : ""}`}
                    tabIndex={0}
                    aria-label={`View Task ${taskLabel(task)}`}
                    onClick={() => setDetailTaskId(task.id)}
                    onKeyDown={(event) => {
                      if (event.target !== event.currentTarget) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setDetailTaskId(task.id);
                      }
                    }}
                  >
                    <TableCell className="min-w-0">
                      <TaskReferenceDisplay task={task} />
                      {task.description && (
                        <span className="block max-w-52 truncate font-sans text-muted-foreground">
                          {task.description}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={task.type} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={task.status} />
                      {isTaskOnCancellationHold(task) && (
                        <span className="mt-1 block text-[11px] font-semibold">
                          On hold · cancellation review
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs">
                      {task.scheduledDate || task.scheduledWindow
                        ? formatTaskScheduleDay(task)
                        : formatDate(task.createdAt)}
                      <span className="block text-muted-foreground">
                        {secondaryTiming(task)}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {task.shipment ? (
                        <>
                          SHP-{task.shipment.shipmentNumber}
                          {task.collectionException && (
                            <span className="block text-xs font-semibold text-amber-700">
                              Missing at collection — dispatcher action needed
                            </span>
                          )}
                          {task.shipment.trackingNumber && (
                            <span className="block max-w-36 truncate text-muted-foreground">
                              {task.shipment.trackingNumber}
                            </span>
                          )}
                        </>
                      ) : (
                        "No Shipment"
                      )}
                    </TableCell>
                    <TableCell>
                      {task.trip ? (
                        <>
                          TRP-{task.trip.tripNumber}
                          <span className="block text-xs text-muted-foreground">
                            {humanizeStatus(task.trip.status)}
                          </span>
                        </>
                      ) : (
                        "No Trip"
                      )}
                    </TableCell>
                    <TableCell>{dispatchCenterName(task)}</TableCell>
                    <TableCell>{personName(task)}</TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      {awaitingReceipt ? (
                        <TaskReceiptAction
                          task={task}
                          pending={receiving || receive.isPending}
                          onReceive={handleReceive}
                        />
                      ) : (
                        <TaskActionsMenu
                          task={task}
                          onView={() => setDetailTaskId(task.id)}
                          onComplete={() => requestComplete(task)}
                          onAssign={() => requestAssign(task)}
                          onUnassign={() => requestUnassign(task)}
                          onReassign={() => requestReassign(task)}
                          onAssignOperator={() => requestAssignOperator(task)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {data && (
          <div className="border-t p-3">
            <TablePager
              page={data.metadata.page}
              pageSize={data.metadata.limit}
              total={data.metadata.total}
              totalPages={Math.max(1, data.metadata.totalPages)}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      {reassignTask && (
        <ReassignTaskDialog
          open
          onOpenChange={(open) => {
            if (!open) setReassignTask(null);
          }}
          taskId={reassignTask.id}
          dialogPresentation="responsive-sheet"
        />
      )}

      {assignTrip && (
        <AssignTaskToTripDialog
          open
          onOpenChange={(open) => {
            if (!open) setAssignTrip(null);
          }}
          taskId={assignTrip.id}
          taskLabel={`${taskLabel(assignTrip)}`}
          dialogPresentation="responsive-sheet"
        />
      )}

      {assignOperator && (
        <AssignTaskOperatorDialog
          open
          onOpenChange={(open) => {
            if (!open) setAssignOperator(null);
          }}
          task={assignOperator}
          dialogPresentation="responsive-sheet"
        />
      )}

      <TaskDetailDialog
        open={!!detailTaskId}
        onOpenChange={(open) => {
          if (!open) closeTaskDetail();
        }}
        taskId={detailTaskId}
        summary={selectedTask}
        actionsPending={actionsPending}
        onComplete={requestComplete}
        onAssignTrip={requestAssign}
        onUnassignTrip={requestUnassign}
        onReassign={requestReassign}
        onAssignOperator={requestAssignOperator}
      />

      <AlertDialog
        open={confirmation !== null}
        onOpenChange={(open) => {
          if (!open && !actionsPending) setConfirmation(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmation?.action === "complete"
                ? "Complete Task?"
                : "Unassign Task from Trip?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation
                ? `${taskLabel(confirmation.task)} ${
                    confirmation.action === "complete"
                      ? "will be marked completed."
                      : "will be removed from its Trip and Operator."
                  }`
                : "Confirm this Task action."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionsPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={actionsPending}
              variant={
                confirmation?.action === "unassign" ? "destructive" : "default"
              }
              onClick={(event) => {
                event.preventDefault();
                if (!confirmation) return;
                if (confirmation.action === "complete") {
                  handleComplete(confirmation.task);
                } else {
                  handleUnassign(confirmation.task);
                }
              }}
            >
              {actionsPending
                ? "Working…"
                : confirmation?.action === "complete"
                  ? "Complete Task"
                  : "Unassign Task"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TaskActionsMenu({
  task,
  onView,
  onComplete,
  onAssign,
  onUnassign,
  onReassign,
  onAssignOperator,
}: {
  task: CarrierTask;
  onView: () => void;
  onComplete: () => void;
  onAssign: () => void;
  onUnassign: () => void;
  onReassign: () => void;
  onAssignOperator: () => void;
}) {
  return (
    <RowMenu label={`Actions for ${taskLabel(task)}`}>
      <RowMenuItem onClick={onView}>View details</RowMenuItem>
      {isTaskOnCancellationHold(task) && (
        <RowMenuItem disabled>On hold · cancellation review</RowMenuItem>
      )}
      {canComplete(task) && (
        <RowMenuItem onClick={onComplete}>Complete Task</RowMenuItem>
      )}
      {!isTaskOnCancellationHold(task) &&
        (task.tripId ? (
          <RowMenuItem onClick={onUnassign}>Unassign from Trip</RowMenuItem>
        ) : (
          <RowMenuItem onClick={onAssign}>Assign to Trip</RowMenuItem>
        ))}
      {!isTaskOnCancellationHold(task) && (
        <RowMenuItem onClick={onReassign}>Reassign Dispatch Center</RowMenuItem>
      )}
      {!task.tripId && canComplete(task) && (
        <RowMenuItem onClick={onAssignOperator}>
          {task.operatorId ? "Reassign Operator" : "Assign Operator"}
        </RowMenuItem>
      )}
    </RowMenu>
  );
}

function TaskFilterFields({
  tripStatus,
  type,
  status,
  onTripStatusChange,
  onTypeChange,
  onStatusChange,
  mobile = false,
}: {
  tripStatus: string;
  type: string;
  status: string;
  onTripStatusChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  mobile?: boolean;
}) {
  const suffix = mobile ? "mobile" : "desktop";
  const fieldsClass = mobile ? "space-y-4" : "flex items-center gap-2";
  const labelClass = mobile ? "block space-y-1" : "block";
  const textClass = mobile ? "text-sm font-medium" : "sr-only";
  const selectClass = mobile ? "w-full" : "w-40";
  return (
    <div className={fieldsClass}>
      <label htmlFor={`task-trip-filter-${suffix}`} className={labelClass}>
        <span className={textClass}>Trip</span>
        <NativeSelect
          id={`task-trip-filter-${suffix}`}
          aria-label="Filter by Trip"
          className={selectClass}
          value={tripStatus}
          onChange={(event) => onTripStatusChange(event.target.value)}
        >
          {TRIP_FILTERS.map((filter) => (
            <option key={filter.value} value={filter.value}>
              {filter.label}
            </option>
          ))}
        </NativeSelect>
      </label>
      <label htmlFor={`task-type-filter-${suffix}`} className={labelClass}>
        <span className={textClass}>Type</span>
        <NativeSelect
          id={`task-type-filter-${suffix}`}
          aria-label="Filter by type"
          className={selectClass}
          value={type}
          onChange={(event) => onTypeChange(event.target.value)}
        >
          <option value="">All types</option>
          {TYPE_FILTERS.map((option) => (
            <option key={option} value={option}>
              {humanizeStatus(option)}
            </option>
          ))}
        </NativeSelect>
      </label>
      <label htmlFor={`task-status-filter-${suffix}`} className={labelClass}>
        <span className={textClass}>Status</span>
        <NativeSelect
          id={`task-status-filter-${suffix}`}
          aria-label="Filter by status"
          className={selectClass}
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
        >
          <option value="">All statuses</option>
          {STATUS_FILTERS.map((option) => (
            <option key={option} value={option}>
              {humanizeStatus(option)}
            </option>
          ))}
        </NativeSelect>
      </label>
    </div>
  );
}
